import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../../../../components/ui/icon'
import { mdiContentCopy, mdiDelete, mdiPencil, mdiPlus } from '@mdi/js'
import {
    ACTIONS,
    QUALIFIED_ACTIONS,
    RESOURCE_ORDER,
    actionVariant,
    isWriteAction,
    permissionCode,
    usePermissionCatalogue,
    useQualifiedActionsByResource,
    useRoleMutations,
    useRoles,
    type Action,
    type BaseAction,
    type QualifiedAction,
    type RoleView,
} from '../../../../api/roles'
import type { Permission } from '../../../../api/schema'
import {
    isSubsetOf,
    useCan,
    usePermissions,
} from '../../../../contexts/PermissionsContext'
import { useLocale } from '../../../../contexts/LocaleContext'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { getLocalizedName } from '../../../../lib/localization'
import { Badge } from '../../../../components/ui/badge'
import { Button } from '../../../../components/ui/button'
import { Checkbox } from '../../../../components/ui/checkbox'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { Textarea } from '../../../../components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { mdiAccountKeyOutline } from '@mdi/js'

// The editor is URL-backed: `?role=<uid>` opens one, `?role=new` opens the
// create form, and `?from=<uid>` prefills it from a built-in ("duplicate as
// custom role"). front/CLAUDE.md — every UI state change is a routing change.
interface RolesSearch {
    role?: string
    from?: string
}

export const Route = createFileRoute('/$org/settings/roles/')({
    component: RolesPage,
    validateSearch: (search: Record<string, unknown>): RolesSearch => ({
        role: typeof search.role === 'string' ? search.role : undefined,
        from: typeof search.from === 'string' ? search.from : undefined,
    }),
})

// A role's permission set as a lookup, so the 13 × 2 matrix does not run a
// linear scan per cell.
type PermissionSet = Set<string>

function toSet(permissions: readonly string[] | undefined): PermissionSet {
    return new Set(permissions ?? [])
}

function RolesPage() {
    const { org } = Route.useParams()
    const { role: selectedParam, from: duplicateFrom } = Route.useSearch()
    const navigate = useNavigate()
    const { t } = useTranslation('roles')
    const { locale } = useLocale()

    const canWrite = useCan('members.write')
    const { permissions: myPermissions } = usePermissions()

    const { data, isLoading } = useRoles(org)
    const roles = useMemo(() => data?.data ?? [], [data])

    const breadcrumbs = useMemo(
        () => [
            { title: t('page.breadcrumb_home'), path: `/${org}` },
            { title: t('page.breadcrumb_settings'), path: `/${org}/settings` },
            { title: t('page.breadcrumb') },
        ],
        [org, t]
    )
    useBreadcrumbs(breadcrumbs)

    const selectedRole = useMemo(
        () => roles.find((r) => r.uid === selectedParam),
        [roles, selectedParam]
    )
    const sourceRole = useMemo(
        () => roles.find((r) => r.uid === duplicateFrom),
        [roles, duplicateFrom]
    )

    const isCreating = selectedParam === 'new'

    const open = (role?: string, from?: string) =>
        navigate({ to: '/$org/settings/roles', params: { org }, search: { role, from } })

    // WHICH resources offer narrowed actions is the backend catalogue's answer,
    // not a list kept here (spec §2). One fetch feeds both the reference matrix
    // and the editor.
    const { byResource: qualifiedByResource, isLoading: qualifiedLoading } =
        useQualifiedActionsByResource(org)

    return (
        <div
            className="container mx-auto py-8 max-w-5xl space-y-8"
            data-testid="roles-page"
        >
            <PageHeader
                icon={SECTION_ICONS.roles}
                title={t('page.title')}
                description={t('page.subtitle', { org })}
                actions={
                <>
                {canWrite && !isCreating && (
                    <Button
                        onClick={() => open('new')}
                        aria-label={t('actions.create')}
                        title={t('actions.create')}
                        data-testid="create-role-btn"
                    >
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('actions.create')}</span>
                    </Button>
                )}
                </>
                }
            />

            {/* Both the reference and the editor paint the narrowed grants from
                the catalogue, so neither may be shown while it is still in
                flight: a matrix drawn without it silently under-reports what a
                role holds. `isLoading || qualifiedLoading` is the one gate. */}
            <PermissionsReference
                org={org}
                roles={roles}
                isLoading={isLoading || qualifiedLoading}
                locale={locale}
                qualifiedByResource={qualifiedByResource}
            />

            {(isCreating || selectedRole) && (
                <RoleEditor
                    key={selectedRole?.uid ?? `new-${duplicateFrom ?? ''}`}
                    org={org}
                    role={selectedRole}
                    prefillFrom={isCreating ? sourceRole : undefined}
                    canWrite={canWrite}
                    myPermissions={myPermissions}
                    qualifiedByResource={qualifiedByResource}
                    qualifiedLoading={qualifiedLoading}
                    onClose={() => open(undefined)}
                    onDuplicate={(uid) => open('new', uid)}
                />
            )}

            <section data-testid="roles-list-section">
                <h2 className="text-lg font-semibold mb-3">{t('list.title')}</h2>
                <p className="text-sm text-muted-foreground mb-3">
                    {t('list.builtin_hint')}
                </p>
                {isLoading ? (
                    <p className="text-muted-foreground" data-testid="roles-loading">
                        {t('list.loading')}
                    </p>
                ) : roles.length === 0 ? (
                    <EmptyState
                        variant="inline"
                        icon={mdiAccountKeyOutline}
                        title={t('list.empty')}
                        data-testid="roles-empty"
                    />
                ) : (
                    <div className="bg-card border border-border rounded-lg divide-y divide-border">
                        {roles.map((role) => (
                            <RoleRow
                                key={role.uid}
                                role={role}
                                locale={locale}
                                canWrite={canWrite}
                                isSelected={role.uid === selectedParam}
                                onOpen={() => open(role.uid)}
                                onDuplicate={() => open('new', role.uid)}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    )
}

function RoleRow({
    role,
    locale,
    canWrite,
    isSelected,
    onOpen,
    onDuplicate,
}: {
    role: RoleView
    locale: string
    canWrite: boolean
    isSelected: boolean
    onOpen: () => void
    onDuplicate: () => void
}) {
    const { t } = useTranslation('roles')
    const name = getLocalizedName(role.name as Record<string, string>, locale) || role.code
    const description = getLocalizedName(
        role.description as Record<string, string> | undefined,
        locale
    )

    return (
        <div
            className={`flex items-center justify-between gap-4 p-4 ${isSelected ? 'bg-muted/50' : ''}`}
            data-testid={`role-row-${role.code}`}
        >
            <button
                type="button"
                onClick={onOpen}
                className="flex-1 min-w-0 text-left"
                data-testid={`role-open-${role.code}`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{name}</span>
                    <code className="text-xs text-muted-foreground font-mono">
                        {role.code}
                    </code>
                    <Badge
                        variant={role.is_builtin ? 'secondary' : 'outline'}
                        data-testid={`role-badge-${role.code}`}
                    >
                        {role.is_builtin
                            ? t('list.builtin_badge')
                            : t('list.custom_badge')}
                    </Badge>
                </div>
                {description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                    <span data-testid={`role-members-count-${role.code}`}>
                        {t('list.members_count', { count: role.members_count })}
                    </span>
                    {' · '}
                    {t('list.permissions_count', { count: role.permissions.length })}
                </p>
            </button>
            <div className="flex items-center gap-1 shrink-0">
                {canWrite && (
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onDuplicate}
                        aria-label={t('actions.duplicate')}
                        title={t('actions.duplicate')}
                        data-testid={`role-duplicate-${role.code}`}
                    >
                        <AppIcon path={mdiContentCopy} size="sm" />
                    </Button>
                )}
                {canWrite && !role.is_builtin && (
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={onOpen}
                        aria-label={t('actions.edit')}
                        title={t('actions.edit')}
                        data-testid={`role-edit-${role.code}`}
                    >
                        <AppIcon path={mdiPencil} size="sm" />
                    </Button>
                )}
            </div>
        </div>
    )
}

function RoleEditor({
    org,
    role,
    prefillFrom,
    canWrite,
    myPermissions,
    qualifiedByResource,
    qualifiedLoading,
    onClose,
    onDuplicate,
}: {
    org: string
    role?: RoleView
    prefillFrom?: RoleView
    canWrite: boolean
    myPermissions: Permission[]
    qualifiedByResource: Record<string, QualifiedAction[]>
    qualifiedLoading: boolean
    onClose: () => void
    onDuplicate: (uid: string) => void
}) {
    const { t } = useTranslation('roles')
    const { t: tAccess } = useTranslation('access')
    const { locale } = useLocale()
    const mutations = useRoleMutations(org)

    const isNew = !role
    // A built-in role is immutable server-side (409 builtin_role_immutable), so
    // the editor shows it read-only and offers duplication instead.
    const readOnly = !canWrite || Boolean(role?.is_builtin)

    const source = role ?? prefillFrom

    const [code, setCode] = useState(
        isNew && prefillFrom ? `${prefillFrom.code}_custom` : (role?.code ?? '')
    )
    const [nameEn, setNameEn] = useState('')
    const [nameFr, setNameFr] = useState('')
    const [descriptionEn, setDescriptionEn] = useState('')
    const [descriptionFr, setDescriptionFr] = useState('')
    const [selected, setSelected] = useState<PermissionSet>(() =>
        toSet(source?.permissions)
    )
    // The asset-scoped half (spec 2026-09-04-03): what a grant of this role
    // pulls in, and whether it may ONLY be granted on an asset.
    const [reach, setReach] = useState<string>(source?.reach ?? 'subtree')
    const [scopedOnly, setScopedOnly] = useState<boolean>(Boolean(source?.scoped_only))
    const [error, setError] = useState<string | null>(null)

    // Synchronize the form when the underlying role changes (front/CLAUDE.md,
    // "State Synchronization" — never a useState initializer for prop sync).
    useEffect(() => {
        const name = (source?.name ?? {}) as Record<string, string>
        const description = (source?.description ?? {}) as Record<string, string>
        setNameEn(name.en ?? '')
        setNameFr(name.fr ?? '')
        setDescriptionEn(description.en ?? '')
        setDescriptionFr(description.fr ?? '')
        setSelected(toSet(source?.permissions))
        setReach(source?.reach ?? 'subtree')
        setScopedOnly(Boolean(source?.scoped_only))
    }, [source])

    const toggle = (permission: Permission, checked: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (checked) next.add(permission)
            else next.delete(permission)
            return next
        })
    }

    const permissions = useMemo(
        () => Array.from(selected) as Permission[],
        [selected]
    )

    // Mirrors the backend's no-escalation rule (authz.IsSubset) so the button
    // explains itself before the request rather than after a 403 — INCLUDING its
    // narrowed-action clause: a caller holding `issues.write` may grant
    // `issues.write_own`. A verbatim `includes` check here is what made
    // "duplicate Resident" un-saveable for every organization-wide admin, since
    // no org-wide bundle carries a qualified code literally.
    const escalates = !isSubsetOf(permissions, myPermissions)

    const handleSave = async () => {
        setError(null)
        try {
            const name: Record<string, string> = { en: nameEn.trim() }
            if (nameFr.trim()) name.fr = nameFr.trim()
            const description: Record<string, string> = {}
            if (descriptionEn.trim()) description.en = descriptionEn.trim()
            if (descriptionFr.trim()) description.fr = descriptionFr.trim()

            if (isNew) {
                await mutations.create.mutateAsync({
                    params: { path: { orgCode: org } },
                    body: {
                        code: code.trim(),
                        name: name as never,
                        description: (Object.keys(description).length
                            ? description
                            : undefined) as never,
                        permissions,
                        reach: reach as never,
                        scoped_only: scopedOnly,
                    },
                })
            } else {
                await mutations.update.mutateAsync({
                    params: { path: { orgCode: org, roleUid: role!.uid } },
                    body: {
                        name: name as never,
                        description: description as never,
                        permissions,
                        reach: reach as never,
                        scoped_only: scopedOnly,
                    },
                })
            }
            onClose()
        } catch (err) {
            setError(errorMessage(err, t))
        }
    }

    const title = isNew
        ? t('editor.create_title')
        : readOnly
          ? t('editor.view_title')
          : t('editor.edit_title')

    const displayName =
        getLocalizedName(role?.name as Record<string, string> | undefined, locale) ||
        role?.code ||
        ''

    return (
        <section
            className="bg-card border border-border rounded-lg p-6 space-y-6"
            data-testid="role-editor"
            // WHICH role the editor is showing. Opening another role is a
            // client-side navigation, so `role-editor` alone cannot tell the
            // previous role's editor from the next one's; this attribute can.
            data-role-code={role?.code ?? 'new'}
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
                    {readOnly && role?.is_builtin && (
                        <p
                            className="text-sm text-muted-foreground mt-1"
                            data-testid="role-editor-readonly-notice"
                        >
                            {t('editor.readonly_notice')}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {role?.is_builtin && canWrite && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onDuplicate(role.uid)}
                            aria-label={t('actions.duplicate')}
                            title={t('actions.duplicate')}
                            data-testid="role-editor-duplicate-btn"
                        >
                            <AppIcon path={mdiContentCopy} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">
                                {t('actions.duplicate')}
                            </span>
                        </Button>
                    )}
                    {role && !role.is_builtin && canWrite && (
                        <DeleteRoleButton
                            org={org}
                            role={role}
                            name={displayName}
                            onDeleted={onClose}
                        />
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClose}
                        data-testid="role-editor-close-btn"
                    >
                        {readOnly ? t('actions.close') : t('actions.cancel')}
                    </Button>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="role-code">{t('editor.code_label')}</Label>
                    <Input
                        id="role-code"
                        value={code}
                        readOnly={!isNew || readOnly}
                        disabled={!isNew || readOnly}
                        placeholder={t('editor.code_placeholder')}
                        onChange={(e) => setCode(e.target.value)}
                        data-testid="role-code-input"
                    />
                    {isNew && (
                        <p className="text-xs text-muted-foreground">
                            {t('editor.code_help')}
                        </p>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="role-name-en">{t('editor.name_en_label')}</Label>
                    <Input
                        id="role-name-en"
                        value={nameEn}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => setNameEn(e.target.value)}
                        data-testid="role-name-en-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="role-name-fr">{t('editor.name_fr_label')}</Label>
                    <Input
                        id="role-name-fr"
                        value={nameFr}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => setNameFr(e.target.value)}
                        data-testid="role-name-fr-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="role-description-en">
                        {t('editor.description_en_label')}
                    </Label>
                    <Textarea
                        id="role-description-en"
                        rows={2}
                        value={descriptionEn}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => setDescriptionEn(e.target.value)}
                        data-testid="role-description-en-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="role-description-fr">
                        {t('editor.description_fr_label')}
                    </Label>
                    <Textarea
                        id="role-description-fr"
                        rows={2}
                        value={descriptionFr}
                        readOnly={readOnly}
                        disabled={readOnly}
                        onChange={(e) => setDescriptionFr(e.target.value)}
                        data-testid="role-description-fr-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="role-reach">{tAccess('reach.label')}</Label>
                    <Select
                        value={reach}
                        onValueChange={setReach}
                        disabled={readOnly}
                    >
                        <SelectTrigger id="role-reach" data-testid="role-reach-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="subtree" data-testid="role-reach-subtree">
                                {tAccess('reach.subtree')}
                            </SelectItem>
                            <SelectItem
                                value="private_plus_common"
                                data-testid="role-reach-private-plus-common"
                            >
                                {tAccess('reach.private_plus_common')}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="role-scoped-only"
                            checked={scopedOnly}
                            disabled={readOnly}
                            onCheckedChange={(checked) => setScopedOnly(checked === true)}
                            data-testid="role-scoped-only-checkbox"
                        />
                        <Label htmlFor="role-scoped-only">{tAccess('role.scoped_only')}</Label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {tAccess('role.scoped_only_help')}
                    </p>
                </div>
            </div>

            <PermissionMatrix
                selected={selected}
                readOnly={readOnly}
                myPermissions={myPermissions}
                qualifiedByResource={qualifiedByResource}
                qualifiedLoading={qualifiedLoading}
                onToggle={toggle}
            />

            {error && (
                <p className="text-sm text-destructive" data-testid="role-editor-error">
                    {error}
                </p>
            )}

            {!readOnly && (
                <div className="flex justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="role-cancel-btn"
                    >
                        {t('actions.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            !nameEn.trim() ||
                            (isNew && code.trim().length < 3) ||
                            escalates ||
                            mutations.create.isPending ||
                            mutations.update.isPending
                        }
                        data-testid="role-save-btn"
                    >
                        {t('actions.save')}
                    </Button>
                </div>
            )}
        </section>
    )
}

function PermissionMatrix({
    selected,
    readOnly,
    myPermissions,
    qualifiedByResource,
    qualifiedLoading,
    onToggle,
}: {
    selected: PermissionSet
    readOnly: boolean
    myPermissions: Permission[]
    qualifiedByResource: Record<string, QualifiedAction[]>
    qualifiedLoading: boolean
    onToggle: (permission: Permission, checked: boolean) => void
}) {
    const { t } = useTranslation('roles')

    // The Limited column exists as soon as ANY resource declares a narrowed
    // action, and each row only fills it when its own resource does. Both
    // answers come from the catalogue, never from a list written here.
    const hasQualified = Object.keys(qualifiedByResource).length > 0

    // Until the catalogue answers, the Limited column cannot be drawn — and a
    // matrix missing it is a matrix that shows `resident` two ticks when the
    // role holds seven. Render nothing rather than an under-count: the
    // `permission-matrix` testid then means "every grant this role holds is on
    // screen", which is what both the reader and the e2e assertions rely on.
    if (qualifiedLoading) {
        return (
            <p
                className="text-sm text-muted-foreground"
                data-testid="permission-matrix-loading"
            >
                {t('editor.matrix_loading')}
            </p>
        )
    }

    return (
        <div className="space-y-2" data-testid="permission-matrix">
            <div>
                <h3 className="text-sm font-semibold text-foreground">
                    {t('editor.matrix_title')}
                </h3>
                <p className="text-xs text-muted-foreground">{t('editor.matrix_help')}</p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border text-left">
                            <th className="py-2 pr-4 font-medium text-muted-foreground">
                                {t('editor.resource_column')}
                            </th>
                            {ACTIONS.map((action) => (
                                <th
                                    key={action}
                                    className="py-2 px-4 font-medium text-muted-foreground w-24"
                                >
                                    {t(`actions.${action}`)}
                                </th>
                            ))}
                            {hasQualified && (
                                <th
                                    className="py-2 px-4 font-medium text-muted-foreground"
                                    data-testid="permission-limited-column"
                                >
                                    {t('editor.limited_column')}
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {RESOURCE_ORDER.map((resource) => (
                            <tr
                                key={resource}
                                className="border-b border-border last:border-b-0"
                                data-testid={`permission-row-${resource}`}
                            >
                                <td className="py-2 pr-4">
                                    <div className="font-medium text-foreground">
                                        {t(`resources.${resource}`)}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {t(`resource_descriptions.${resource}`)}
                                    </div>
                                </td>
                                {ACTIONS.map((action: BaseAction) => (
                                    <td key={action} className="py-2 px-4">
                                        <PermissionCheckbox
                                            resource={resource}
                                            action={action}
                                            selected={selected}
                                            readOnly={readOnly}
                                            myPermissions={myPermissions}
                                            onToggle={onToggle}
                                        />
                                    </td>
                                ))}
                                {hasQualified && (
                                    <td
                                        className="py-2 px-4"
                                        data-testid={`permission-limited-${resource}`}
                                    >
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                            {(qualifiedByResource[resource] ?? []).map(
                                                (action) => (
                                                    <label
                                                        key={action}
                                                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                                                    >
                                                        <PermissionCheckbox
                                                            resource={resource}
                                                            action={action}
                                                            selected={selected}
                                                            readOnly={readOnly}
                                                            myPermissions={myPermissions}
                                                            onToggle={onToggle}
                                                        />
                                                        <span>{t(`actions.${action}`)}</span>
                                                    </label>
                                                )
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

/**
 * One permission checkbox — the same control for a base action and for a
 * narrowed one, because the backend treats them as distinct grants of equal
 * standing: `issues.read` and `issues.read_own` are two rows in the bundle, and
 * neither implies the other.
 */
function PermissionCheckbox({
    resource,
    action,
    selected,
    readOnly,
    myPermissions,
    onToggle,
}: {
    resource: string
    action: Action
    selected: PermissionSet
    readOnly: boolean
    myPermissions: Permission[]
    onToggle: (permission: Permission, checked: boolean) => void
}) {
    const { t } = useTranslation('roles')
    const { org } = Route.useParams()
    const { data: catalogue } = usePermissionCatalogue(org)
    const code = permissionCode(resource, action)
    // A resource may offer `read` only (billing): the catalogue lists no
    // `billing.write`, so that cell shows a dash instead of a checkbox that
    // could never be granted. Until the catalogue answers the box is drawn.
    if (catalogue && !catalogue.data.some((entry) => entry.code === code)) {
        return (
            <span
                className="text-muted-foreground"
                aria-hidden="true"
                data-testid={`permission-unavailable-${code}`}
            >
                —
            </span>
        )
    }
    // A permission the caller cannot pass on can never be granted (403
    // permission_escalation), so it is disabled rather than silently refused.
    // `isSubsetOf` is the same test the backend runs, narrowed actions included.
    const beyondCaller = !isSubsetOf([code], myPermissions)

    return (
        <Checkbox
            checked={selected.has(code)}
            disabled={readOnly || beyondCaller}
            aria-label={`${t(`resources.${resource}`)} — ${t(`actions.${action}`)}`}
            title={
                beyondCaller && !readOnly ? t('errors.permission_escalation') : undefined
            }
            onCheckedChange={(checked) => onToggle(code, checked)}
            data-testid={`permission-checkbox-${code}`}
        />
    )
}

/**
 * The read-only half of the page: what the words mean, and what each role can
 * do side by side.
 *
 * It sits ABOVE the editor on purpose. This page is the only place a member can
 * learn what a role lets someone do, and until now it answered that question
 * one modal at a time — with the narrowed permissions invisible entirely.
 */
function PermissionsReference({
    org,
    roles,
    isLoading,
    locale,
    qualifiedByResource,
}: {
    org: string
    roles: RoleView[]
    isLoading: boolean
    locale: string
    qualifiedByResource: Record<string, QualifiedAction[]>
}) {
    const { t } = useTranslation('roles')
    const { t: tAccess } = useTranslation('access')

    // The legend lists the actions the catalogue actually declares, base ones
    // first — so a narrowed action added in internal/authz shows up here
    // without a frontend change, and one that is removed stops being explained.
    const declaredActions = useMemo<Action[]>(() => {
        const declared = new Set<string>()
        for (const actions of Object.values(qualifiedByResource)) {
            for (const action of actions) declared.add(action)
        }
        return [
            ...ACTIONS,
            ...QUALIFIED_ACTIONS.filter((action) => declared.has(action)),
        ]
    }, [qualifiedByResource])

    return (
        <section className="space-y-4" data-testid="permissions-reference">
            <div>
                <h2 className="text-lg font-semibold">{t('legend.title')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                    {t('legend.description')}
                </p>
            </div>

            <div
                className="bg-card border border-border rounded-lg p-4 space-y-4"
                data-testid="permissions-legend"
            >
                <div>
                    <h3 className="text-sm font-semibold">{t('legend.actions_title')}</h3>
                    <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                        {declaredActions.map((action) => (
                            <div key={action} data-testid={`action-legend-${action}`}>
                                <dt className="text-sm font-medium text-foreground flex items-baseline gap-2">
                                    {t(`actions.${action}`)}
                                    <code className="text-xs font-mono text-muted-foreground">
                                        {action}
                                    </code>
                                </dt>
                                <dd className="text-xs text-muted-foreground">
                                    {t(`action_descriptions.${action}`)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    <p className="text-xs text-muted-foreground mt-3">
                        {t('legend.independent')}
                    </p>
                </div>

                <div>
                    <h3 className="text-sm font-semibold">
                        {t('reach_descriptions.title')}
                    </h3>
                    <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                        <div data-testid="reach-legend-subtree">
                            <dt className="text-sm font-medium text-foreground">
                                {tAccess('reach.subtree')}
                            </dt>
                            <dd className="text-xs text-muted-foreground">
                                {t('reach_descriptions.subtree')}
                            </dd>
                        </div>
                        <div data-testid="reach-legend-private-plus-common">
                            <dt className="text-sm font-medium text-foreground">
                                {tAccess('reach.private_plus_common')}
                            </dt>
                            <dd className="text-xs text-muted-foreground">
                                {t('reach_descriptions.private_plus_common')}
                            </dd>
                        </div>
                        <div data-testid="reach-legend-scoped-only">
                            <dt className="text-sm font-medium text-foreground">
                                {tAccess('role.scoped_only')}
                            </dt>
                            <dd className="text-xs text-muted-foreground">
                                {t('scoped_only_description')}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <RoleComparisonMatrix
                org={org}
                roles={roles}
                isLoading={isLoading}
                locale={locale}
                qualifiedByResource={qualifiedByResource}
            />
        </section>
    )
}

/** What a role holds on one resource, reduced to what a cell has to paint. */
interface CellState {
    codes: string[]
    token: 'none' | 'read' | 'write' | 'read_write'
    narrowed: QualifiedAction[]
}

function cellState(
    permissions: readonly string[],
    resource: string,
    qualified: QualifiedAction[]
): CellState {
    const held = new Set(permissions)
    const codes = permissions
        .filter((code) => code.startsWith(`${resource}.`))
        .slice()
        .sort()
    const hasRead = held.has(`${resource}.read`)
    const hasWrite = held.has(`${resource}.write`)
    const narrowed = qualified.filter((action) => held.has(`${resource}.${action}`))
    const token: CellState['token'] =
        hasRead && hasWrite
            ? 'read_write'
            : hasRead
              ? 'read'
              : hasWrite
                ? 'write'
                : 'none'

    return { codes, token, narrowed }
}

/**
 * Resources × roles, read-only. The API decided the column order (built-ins
 * strongest first, then custom by code) and this renders it as received —
 * re-sorting here would make the frontend a second ordering authority.
 *
 * The table scrolls inside its OWN container: with nine built-ins plus whatever
 * an org added, it is wider than any phone, and the page body must never scroll
 * horizontally.
 */
function RoleComparisonMatrix({
    org,
    roles,
    isLoading,
    locale,
    qualifiedByResource,
}: {
    org: string
    roles: RoleView[]
    isLoading: boolean
    locale: string
    qualifiedByResource: Record<string, QualifiedAction[]>
}) {
    const { t } = useTranslation('roles')

    if (isLoading) {
        return (
            <p className="text-muted-foreground text-sm" data-testid="role-matrix-loading">
                {t('matrix.loading')}
            </p>
        )
    }
    if (roles.length === 0) return null

    return (
        <div className="space-y-2">
            <div>
                <h3 className="text-sm font-semibold">{t('matrix.title')}</h3>
                <p className="text-xs text-muted-foreground">{t('matrix.help')}</p>
            </div>
            <div
                className="overflow-x-auto border border-border rounded-lg bg-card"
                data-testid="role-matrix-scroll"
            >
                <table className="text-sm min-w-max" data-testid="role-matrix">
                    <thead>
                        <tr className="border-b border-border text-left">
                            <th className="py-2 px-3 font-medium text-muted-foreground sticky left-0 bg-card z-10">
                                {t('matrix.resource_column')}
                            </th>
                            {roles.map((role) => (
                                <th
                                    key={role.uid}
                                    className="py-2 px-3 font-medium text-center align-bottom"
                                    data-testid={`role-matrix-column-${role.code}`}
                                >
                                    <Link
                                        to="/$org/settings/roles"
                                        params={{ org }}
                                        search={{ role: role.uid }}
                                        className="hover:underline"
                                        data-testid={`role-matrix-link-${role.code}`}
                                    >
                                        {getLocalizedName(
                                            role.name as Record<string, string>,
                                            locale
                                        ) || role.code}
                                    </Link>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {RESOURCE_ORDER.map((resource) => (
                            <tr
                                key={resource}
                                className="border-b border-border last:border-b-0"
                                data-testid={`role-matrix-row-${resource}`}
                            >
                                <td className="py-2 px-3 sticky left-0 bg-card z-10 min-w-56 max-w-72">
                                    <div className="font-medium text-foreground">
                                        {t(`resources.${resource}`)}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {t(`resource_descriptions.${resource}`)}
                                    </div>
                                </td>
                                {roles.map((role) => (
                                    <MatrixCell
                                        key={role.uid}
                                        role={role}
                                        resource={resource}
                                        qualified={qualifiedByResource[resource] ?? []}
                                    />
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <ul className="text-xs text-muted-foreground space-y-0.5">
                <li>{t('matrix.legend_none')}</li>
                <li>{t('matrix.legend_read')}</li>
                <li>{t('matrix.legend_write')}</li>
                <li>{t('matrix.legend_variants')}</li>
            </ul>
        </div>
    )
}

function MatrixCell({
    role,
    resource,
    qualified,
}: {
    role: RoleView
    resource: string
    qualified: QualifiedAction[]
}) {
    const { t } = useTranslation('roles')
    const state = cellState(role.permissions ?? [], resource, qualified)
    const empty = state.token === 'none' && state.narrowed.length === 0

    return (
        <td
            className="py-2 px-3 text-center align-middle"
            // The compact token is the summary; the exact codes are one hover
            // away, so the table stays readable without lying by omission.
            title={state.codes.length > 0 ? state.codes.join(', ') : undefined}
            data-testid={`role-matrix-cell-${role.code}-${resource}`}
        >
            {empty ? (
                <span className="text-muted-foreground">{t('matrix.none')}</span>
            ) : (
                <div className="flex flex-wrap items-center justify-center gap-1">
                    {state.token !== 'none' && (
                        <span
                            className="font-mono font-medium text-foreground"
                            data-testid={`role-matrix-token-${role.code}-${resource}`}
                        >
                            {t(`matrix.${state.token}`)}
                        </span>
                    )}
                    {state.narrowed.map((action) => (
                        <Badge
                            key={action}
                            variant="outline"
                            className="px-1.5 py-0 text-[10px] font-normal gap-0.5"
                            title={`${resource}.${action} — ${t(`action_descriptions.${action}`)}`}
                            data-testid={`role-matrix-badge-${role.code}-${resource}-${action}`}
                        >
                            {isWriteAction(action) && (
                                <AppIcon path={mdiPencil} size="xs" aria-hidden />
                            )}
                            {t(`variants.${actionVariant(action)}`)}
                        </Badge>
                    ))}
                </div>
            )}
        </td>
    )
}

function DeleteRoleButton({
    org,
    role,
    name,
    onDeleted,
}: {
    org: string
    role: RoleView
    name: string
    onDeleted: () => void
}) {
    const { t } = useTranslation('roles')
    const mutations = useRoleMutations(org)
    const [open, setOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Deleting a role still held by someone is refused with 409 `role_in_use`;
    // the control is hidden rather than offered-and-refused.
    if (role.members_count > 0) {
        return (
            <Button
                variant="destructive"
                size="icon"
                disabled
                aria-label={t('delete.blocked_in_use')}
                title={t('delete.blocked_in_use')}
                data-testid={`role-delete-${role.code}`}
            >
                <AppIcon path={mdiDelete} size="sm" />
            </Button>
        )
    }

    const handleDelete = async () => {
        setError(null)
        try {
            await mutations.remove.mutateAsync({
                params: { path: { orgCode: org, roleUid: role.uid } },
            })
            setOpen(false)
            onDeleted()
        } catch (err) {
            setError(errorMessage(err, t))
        }
    }

    return (
        <>
            <Button
                variant="destructive"
                size="icon"
                onClick={() => setOpen(true)}
                aria-label={t('actions.delete')}
                title={t('actions.delete')}
                data-testid={`role-delete-${role.code}`}
            >
                <AppIcon path={mdiDelete} size="sm" />
            </Button>
            <AlertDialog open={open} onOpenChange={setOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('delete.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('delete.description', { name, org })}
                            {error ? ` — ${error}` : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="role-delete-cancel">
                            {t('delete.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            data-testid="role-delete-confirm"
                        >
                            {t('delete.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

// errorMessage turns an API failure into a translated sentence. The backend's
// error codes are the contract; the raw title/JSON is never shown.
function errorMessage(
    err: unknown,
    t: (key: string) => string
): string {
    const known = [
        'role_code_exists',
        'invalid_role_code',
        'builtin_role_immutable',
        'role_in_use',
        'permission_escalation',
        'permission_denied',
    ]
    const raw =
        err && typeof err === 'object' && 'code' in err
            ? String((err as { code: unknown }).code)
            : ''
    if (raw === 'permission_denied') return t('permission_denied.description')
    if (known.includes(raw)) return t(`errors.${raw}`)
    return t('errors.generic')
}
